import type { Film, Quality } from './api'

export interface MediaSourceInfo {
  quality: Quality
  url: string
  resolution: string | null
  host: string
}

export function safeExternalHttpUrl(value: string) {
  try {
    const normalized = value.trim().startsWith('//') ? `https:${value.trim()}` : value.trim()
    const url = new URL(normalized)
    const http = url.protocol === 'https:' || url.protocol === 'http:'
    return http && url.hostname && !url.username && !url.password ? url.toString() : null
  } catch {
    return null
  }
}

export function sourceForQuality(film: Film, quality: Quality) {
  if (quality === 'HIGH') return film.highUrl || film.normalUrl
  if (quality === 'LOW') return film.lowUrl || film.normalUrl
  return film.normalUrl
}

export function sourceResolution(url: string) {
  const dimensions = url.match(/(\d{3,4})x(\d{3,4})/i)
  if (dimensions) return `${dimensions[1]} × ${dimensions[2]}`
  const height = url.match(/(?:^|[._/-])(2160|1440|1080|720|540|480|360|270)(?:p|[._/-])/i)?.[1]
  return height ? `${height}p` : null
}

export function mediaSources(film: Film): MediaSourceInfo[] {
  return ([
    ['HIGH', film.highUrl],
    ['NORMAL', film.normalUrl],
    ['LOW', film.lowUrl],
  ] as Array<[Quality, string]>).filter(([, url]) => Boolean(url)).map(([quality, url]) => {
    const normalizedUrl = safeExternalHttpUrl(url) ?? url
    try {
      return { quality, url: normalizedUrl, resolution: sourceResolution(normalizedUrl), host: new URL(normalizedUrl).hostname }
    } catch {
      return { quality, url: normalizedUrl, resolution: sourceResolution(normalizedUrl), host: '' }
    }
  })
}

export function selectedSourceSummary(film: Film, quality: Quality, unknownResolution: string) {
  const url = sourceForQuality(film, quality)
  if (!url) return unknownResolution
  try {
    const normalizedUrl = safeExternalHttpUrl(url) ?? url
    const parsed = new URL(normalizedUrl)
    const resolution = sourceResolution(normalizedUrl) ?? unknownResolution
    const bitrate = normalizedUrl.match(/(\d{3,5})kbit/i)?.[1]
    return [resolution, bitrate ? `${Number(bitrate).toLocaleString('de-DE')} kbit/s` : null, parsed.hostname]
      .filter(Boolean).join(' · ')
  } catch {
    return sourceResolution(url) ?? unknownResolution
  }
}
